/**
 * The categories offered when Ask MAHI opens.
 *
 * Most people opening that panel do not want a conversation — they want
 * sarees. A tap here goes straight to `/inventory/browse` and lands on
 * products in about 100ms, with no LLM and no clarifying question. The chat is
 * underneath for anyone who wants to describe something instead.
 *
 * `key` must be a real value from taxonomy.py's `Category` enum. Anything else
 * is dropped server-side rather than filtered on (a filter no product matches
 * would return an empty grid), so a typo here shows up as a category that
 * silently returns everything.
 *
 * Two lists, not one filtered at render time, because "trimmed by what this
 * catalog actually stocks" is a real measurement and the two halves of the
 * catalogue stock different things. `WOMENS_CATEGORY_KEYS` mirrors
 * `services/mahi/taxonomy.WOMENS_ONLY_CATEGORIES` on the backend — a category
 * that means "women's" there means the same thing here, which is what keeps
 * a menswear shopper from ever being offered a chip whose tap comes back
 * "nothing in sarees right now".
 */
export const BROWSE_CATEGORIES = [
  { key: 'saree', label: 'Sarees' },
  { key: 'kurta_set', label: 'Kurta sets' },
  { key: 'lehenga', label: 'Lehengas' },
  { key: 'dress', label: 'Dresses' },
  { key: 'anarkali', label: 'Anarkalis' },
  { key: 'co_ord_set', label: 'Co-ords' },
  { key: 'gown', label: 'Gowns' },
  { key: 'kurta', label: 'Kurtas' },
  { key: 'top', label: 'Tops' },
  { key: 'suit_set', label: 'Suit sets' },
  { key: 'jumpsuit', label: 'Jumpsuits' },
  { key: 'skirt', label: 'Skirts' },
];

// Real menswear stock, measured the same way the list above was ("Ordered and
// trimmed by what this catalog actually stocks") — checked against the live
// catalogue (`stg_mahi_inventory`, 2026-09-12), where every one of these had
// hundreds to thousands of `gender=male` rows (shirt alone: 3,500).
export const BROWSE_CATEGORIES_MALE = [
  { key: 'shirt', label: 'Shirts' },
  { key: 'tshirt', label: 'T-shirts' },
  { key: 'trousers', label: 'Trousers' },
  { key: 'jeans', label: 'Jeans' },
  { key: 'jacket', label: 'Jackets' },
  { key: 'kurta', label: 'Kurtas' },
  { key: 'suit_set', label: 'Suit sets' },
  { key: 'kurta_set', label: 'Kurta sets' },
  { key: 'co_ord_set', label: 'Co-ords' },
  { key: 'shorts', label: 'Shorts' },
  { key: 'top', label: 'Tops' },
];

/**
 * The chips to show, for this person's resolved feed gender.
 *
 * `gender` should be whatever `prefetch.js`'s `feedGenderFor(user)` (or the
 * equivalent backend `feed.resolve`) already produced — this does not
 * re-derive it, the same way `discovery_prefs.options(gender)` on the
 * backend does not. `''` ("everything") and anything unrecognised fall back
 * to the general list, since the catalogue has no men's-only category to
 * lose by staying broad.
 */
export const browseCategoriesFor = (gender) => (
  gender === 'male' ? BROWSE_CATEGORIES_MALE : BROWSE_CATEGORIES
);

export const labelForCategory = (key) =>
  BROWSE_CATEGORIES.find((c) => c.key === key)?.label
  || BROWSE_CATEGORIES_MALE.find((c) => c.key === key)?.label
  || key;
